import { FlexRow, FullContainer, StyledSearchBar, useDebouncedSearch } from '@gorila-shared-ui/components';
import { useCallback } from 'react';
import { useStyles } from '../../../hooks/useStyles';
import AssetList from './AssetList';
import { useAssets } from './hooks/useAssets';

type Props = {
  assignedAssetIds: string[];
  onAssignedChange: (assetIds: string[]) => void;
  required?: boolean;
};
export function AssetListPicker({ assignedAssetIds, onAssignedChange, required }: Readonly<Props>) {
  const { theme, css, classes } = useStyles();
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch('');
  const {
    selectedAssets,
    unselectedAssets,
    selectedAssetHasNext,
    unselectedAssetHasNext,
    loadMoreSelectedAssets,
    loadMoreUnselectedAssets,
  } = useAssets(debouncedSearch, assignedAssetIds);

  const isAssetsSelectLoaded = (index: number) => !selectedAssetHasNext || index < (selectedAssets?.length || 0);
  const assetSelectedCount = selectedAssetHasNext ? (selectedAssets?.length ?? 0) + 1 : selectedAssets?.length ?? 0;

  const isAssetsUnselectLoaded = (index: number) => !unselectedAssetHasNext || index < (unselectedAssets?.length || 0);
  const assetUnselectedCount = unselectedAssetHasNext
    ? (unselectedAssets?.length ?? 0) + 1
    : unselectedAssets?.length ?? 0;

  const onSelectAsset = useCallback(
    (assetId: string) => {
      onAssignedChange([...assignedAssetIds, assetId]);
    },
    [assignedAssetIds, onAssignedChange]
  );

  const onDeselectAsset = useCallback(
    (assetId: string) => {
      onAssignedChange(assignedAssetIds?.filter((id) => assetId !== id) || []);
    },
    [assignedAssetIds, onAssignedChange]
  );

  const onSelectAllAssets = useCallback(() => {
    onAssignedChange([...(assignedAssetIds || []), ...(unselectedAssets?.map((asset) => asset._id) || [])]);
    setSearch('');
  }, [assignedAssetIds, unselectedAssets, onAssignedChange]);

  const onDeselectAllAssets = useCallback(() => {
    if (search) {
      const assetsSelection = assignedAssetIds?.filter((id) => !selectedAssets?.some((asset) => asset._id === id));
      onAssignedChange(assetsSelection || []);
    } else {
      onAssignedChange([]);
    }
    setSearch('');
  }, [assignedAssetIds, selectedAssets, search, onAssignedChange]);

  if (!selectedAssets && !unselectedAssets) return null;

  return (
    <FullContainer
      flexible
      classNames={`${classes.flexFill} ${classes.verticalFlex} ${css({ gap: theme.sizing.scale400 })}`}
    >
      <StyledSearchBar
        placeholder="Buscar activo"
        name="assets-search"
        value={search}
        onChange={(event) => setSearch(event)}
        clearable
        noMargin
      />
      <FlexRow classNames={`${classes.flexFill}`}>
        <AssetList
          title="Disponibles"
          assets={unselectedAssets}
          assetCount={assetUnselectedCount}
          isItemLoaded={isAssetsUnselectLoaded}
          loadMoreItems={loadMoreUnselectedAssets}
          onItemClick={onSelectAsset}
          onAllClick={onSelectAllAssets}
          emptyStateTitle={search ? 'No se encontraron resultados' : 'No tienes ningún activo por asignar'}
          emptyStateDescription={search ? 'Intenta con otra búsqueda' : undefined}
          type="selectable"
        />
        <AssetList
          title="Asignados"
          assets={selectedAssets}
          assetCount={assetSelectedCount}
          isItemLoaded={isAssetsSelectLoaded}
          loadMoreItems={loadMoreSelectedAssets}
          onItemClick={onDeselectAsset}
          onAllClick={onDeselectAllAssets}
          emptyStateTitle={search ? 'No se encontraron resultados' : 'No tienes ningún activo asignado'}
          emptyStateDescription={search ? 'Intenta con otra búsqueda' : undefined}
          type="deselectable"
          totalAssets={assignedAssetIds.length}
        />
      </FlexRow>
    </FullContainer>
  );
}
